import {
  Button,
  Card,
  Descriptions,
  Divider,
  Dropdown,
  Image,
  List,
  Menu,
  message,
  Modal,
  Table,
  Tag,
  Tooltip,
  Select,
  Form,
  Row,
  Col,
  DatePicker,
  Input,
} from "antd"
import * as API from "common/api"
import * as COMMONS from "common/common"
import moment from "moment"
import "moment/locale/ja"
import React, { useEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet-async"
import { FaEye, FaChevronDown, FaFileCsv, FaTrashAlt } from "react-icons/fa"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useNavigate } from "react-router-dom"
import styled from "styled-components"
import fileDownload from "js-file-download"
import useResizeObserver from "@react-hook/resize-observer"
import { useCSVReader } from "react-papaparse"

moment.updateLocale("ja")

const CustomDatePicker = styled(DatePicker)`
  .ant-picker-input > input {
    text-align: center;
  }
`

const CustomTable = styled(Table)`
  .ant-table-selection-extra {
    position: initial;
    margin: 0;
    padding: 0;
  }

  .ant-table-ping-right,
  .ant-table-ping-left tr th.ant-table-cell {
    cursor: grab;
    cursor: -webkit-grab;
  }

  tr.active th.ant-table-cell {
    cursor: grabbing;
    cursor: -webkit-grabbing;
  }
`

const isActionAvailable = (status) => {
  return (
    status !== COMMONS.SHIPMENT_STATUS_CANCEL_VALUE &&
    status !== COMMONS.SHIPMENT_STATUS_COMPLETE_VALUE
  )
}

const useSize = (target) => {
  const [size, setSize] = React.useState()

  React.useLayoutEffect(() => {
    setSize(target.current.getBoundingClientRect())
  }, [target])

  useResizeObserver(target, (entry) => setSize(entry.contentRect))
  return size
}

const { Option } = Select

const Shipments = (props) => {
  const { systemSettings } = props

  const navigate = useNavigate()

  const isMountedRef = COMMONS.USE_IS_MOUNTED_REF()
  const queryClient = useQueryClient()
  const [shipmentsFilterForm] = Form.useForm()
  const { CSVReader } = useCSVReader()

  const tableRef = useRef(null)
  const size = useSize(tableRef)
  const [isScrollableHintVisible, setIsScrollableHintVisible] = useState(false)

  const [pointSettings, setPointSettings] = useState({})
  const [shipments, setShipments] = useState([])
  const [products, setProducts] = useState([])
  const [currentShipment, setCurrentShipment] = useState(undefined)
  const [csvShipments, setCsvShipments] = useState([])
  const [csvHasError, setCsvHasError] = useState(false)

  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false)
  const [isImportShipmentModalVisible, setIsImportShipmentModalVisible] =
    useState(false)

  const [paginationPerPage, setPaginationPerPage] = useState(20)
  const [paginationPage, setPaginationPage] = useState(1)
  const [paginationTotal, setPaginationTotal] = useState(0)
  const [paginationSort, setPaginationSort] = useState("desc")
  const [paginationSortKey, setPaginationSortKey] = useState(
    "transactionDateTime",
  )

  const [filterTransactionHeadId, setFilterTransactionHeadId] =
    useState(undefined)
  const [filterName, setFilterName] = useState(undefined)
  const [filterEmail, setFilterEmail] = useState(undefined)
  const [filterTelephone, setFilterTelephone] = useState(undefined)
  const [filterAddress, setFilterAddress] = useState(undefined)
  const [filterOrderDateFrom, setFilterOrderDateFrom] = useState(undefined)
  const [filterOrderDateTo, setFilterOrderDateTo] = useState(undefined)
  const [filterShipmentStatus, setFilterShipmentStatus] = useState(undefined)

  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRowStatus, setSelectedRowStatus] = useState(undefined)

  useQuery([API.QUERY_KEY_ADMIN_POINT_SETTINGS], API.ADMIN_GET_POINT_SETTINGS, {
    onSuccess: (response) => {
      if (isMountedRef.current) {
        setPointSettings(response?.data || undefined)
      }
    },
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  useQuery(
    [API.QUERY_KEY_ADMIN_PRODUCTS_LIST, {}],
    () => API.ADMIN_GET_PRODUCTS_LIST(),
    {
      onSuccess: (response) => {
        if (isMountedRef.current) {
          setProducts(response?.data || [])
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    },
  )

  useQuery(
    [
      API.QUERY_KEY_ADMIN_SHIPMENTS,
      {
        paginationPerPage: paginationPerPage,
        paginationPage: paginationPage,
        paginationSort: paginationSort,
        paginationSortKey: paginationSortKey,
        filterTransactionHeadId: filterTransactionHeadId,
        filterName: filterName,
        filterEmail: filterEmail,
        filterTelephone: filterTelephone,
        filterAddress: filterAddress,
        filterOrderDateFrom: filterOrderDateFrom,
        filterOrderDateTo: filterOrderDateTo,
        filterShipmentStatus: filterShipmentStatus,
      },
    ],
    () =>
      API.ADMIN_GET_SHIPMENTS(
        paginationPerPage,
        paginationPage,
        paginationSort,
        paginationSortKey,
        filterTransactionHeadId,
        filterName,
        filterEmail,
        filterTelephone,
        filterAddress,
        filterOrderDateFrom,
        filterOrderDateTo,
        filterShipmentStatus,
      ),
    {
      keepPreviousData: true,
      onSuccess: (response) => {
        if (isMountedRef.current) {
          if (response?.data?.rows && response?.data?.rows?.length > 0) {
            setShipments(response.data.rows)
          } else {
            setShipments([])
          }

          setPaginationPerPage(response.data.pp || 20)
          setPaginationPage(response.data.p || 1)
          setPaginationTotal(response?.data?.count || 0)
          setPaginationSort(response?.data?.sort || "desc")
          setPaginationSortKey(response?.data?.sortKey || "transactionDateTime")
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    },
  )

  const updateShipmentMutation = useMutation(API.ADMIN_UPDATE_SHIPMENT, {
    onSuccess: () => {
      if (isMountedRef.current) {
        message.success(COMMONS.SUCCESS_UPDATE_MSG)
        queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_SHIPMENTS)
      }
    },
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  const exportShipmentCSVMutation = useMutation(API.ADMIN_EXPORT_SHIPMENT_CSV, {
    onSuccess: (response) => {
      if (isMountedRef.current) {
        if (response?.data) {
          fileDownload(
            "\uFEFF" + response.data,
            `${moment().format("YYYYMMDDHHmm")}_配達リスト.csv`,
            "text/csv",
          )
        }
      }
    },
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  const importShipmentsMutation = useMutation(API.ADMIN_IMPORT_SHIPMENTS, {
    onSuccess: () => {
      if (isMountedRef.current) {
        message.success(COMMONS.SUCCESS_IMPORT_MSG)
        queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_SHIPMENTS)
        hideImportShipmentModal()
      }
    },
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  useEffect(() => {
    const slider = document.querySelector("thead.ant-table-thead tr")
    const body = document.querySelector(".ant-table-container .ant-table-body")
    let isDown = false
    let startX
    let scrollLeft

    slider.addEventListener("mousedown", (e) => {
      isDown = true
      slider.classList.add("active")
      startX = e.pageX - body.offsetLeft
      scrollLeft = body.scrollLeft
    })

    slider.addEventListener("mouseleave", () => {
      isDown = false
      slider.classList.remove("active")
    })

    slider.addEventListener("mouseup", () => {
      isDown = false
      slider.classList.remove("active")
    })

    slider.addEventListener("mousemove", (e) => {
      if (!isDown) {
        return
      }

      e.preventDefault()
      const x = e.pageX - body.offsetLeft
      const walk = (x - startX) * 3 //scroll-fast
      body.scrollLeft = scrollLeft - walk
    })
  }, [])

  useEffect(() => {
    if (
      tableRef?.current?.querySelector(".ant-table-ping-right") ||
      tableRef?.current?.querySelector(".ant-table-ping-left")
    ) {
      setIsScrollableHintVisible(true)
    } else {
      setIsScrollableHintVisible(false)
    }
  }, [size])

  const showShipmentStatusChangeConfirmation = (status, paramData) => {
    Modal.confirm({
      title: "確認",
      width: 500,
      content: (
        <>
          <p>
            ステータスを
            <span
              className="font-bold"
              style={{
                color: COMMONS.GET_SHIPMENT_STATUS_COLOR(status),
              }}
            >
              {COMMONS.GET_SHIPMENT_STATUS_VALUE(status)}
            </span>
            に変更します、よろしいですか？
          </p>
          {status === COMMONS.SHIPMENT_STATUS_CANCEL_VALUE ? (
            <p className="mt-4 text-red-600">
              ※返金対応はGMO管理画面から行ってください
              <br />
              ※ポイントの返還はスマレジ管理画面から行ってください。
            </p>
          ) : (
            ""
          )}
        </>
      ),
      okText: "変更する",
      okButtonProps: {
        size: "large",
        type: "primary",
      },
      cancelText: "閉じる",
      cancelButtonProps: {
        size: "large",
      },
      centered: true,
      onOk() {
        updateShipmentMutation.mutate(paramData)
      },
    })
  }

  const showShipmentStatusChangeConflictConfirmation = (status, paramData) => {
    Modal.confirm({
      title: "確認",
      width: 450,
      content: (
        <>
          <p>
            「
            <span
              className="font-bold"
              style={{
                color: COMMONS.SHIPMENT_STATUS_CANCEL_COLOR,
              }}
            >
              送り状番号
            </span>
            」がないですが、ステータスを
            <span
              className="font-bold"
              style={{
                color: COMMONS.GET_SHIPMENT_STATUS_COLOR(status),
              }}
            >
              {COMMONS.GET_SHIPMENT_STATUS_VALUE(status)}
            </span>
            に変更してよろしいですか？
          </p>
        </>
      ),
      okText: "変更する",
      okButtonProps: {
        size: "large",
        type: "primary",
      },
      cancelText: "閉じる",
      cancelButtonProps: {
        size: "large",
      },
      centered: true,
      onOk() {
        updateShipmentMutation.mutate(paramData)
      },
    })
  }

  const handleStatusChange = (shipmentId, trackingId, shipmentStatus) => {
    const paramData = {
      shipmentStatus: shipmentStatus,
      shipmentIds: [shipmentId],
    }

    if (isActionAvailable(shipmentStatus)) {
      updateShipmentMutation.mutate(paramData)
    } else {
      if (
        shipmentStatus === COMMONS.SHIPMENT_STATUS_COMPLETE_VALUE &&
        !trackingId
      ) {
        showShipmentStatusChangeConflictConfirmation(shipmentStatus, paramData)
      } else {
        showShipmentStatusChangeConfirmation(shipmentStatus, paramData)
      }
    }
  }

  const handleBatchStatusChange = () => {
    if (selectedRowStatus) {
      const paramData = {
        shipmentStatus: selectedRowStatus,
        shipmentIds: selectedRowKeys,
      }

      if (isActionAvailable(selectedRowStatus)) {
        updateShipmentMutation.mutate(paramData)
      } else {
        if (selectedRowStatus === COMMONS.SHIPMENT_STATUS_COMPLETE_VALUE) {
          const checkedShipments = selectedRowKeys.filter(
            (shipmentId) =>
              shipments.find((s) => s?.Shipment?.shipmentId === shipmentId)
                ?.Shipment?.trackingId,
          )

          console.log(checkedShipments.length)
          console.log(selectedRowKeys.length)

          if (checkedShipments.length === selectedRowKeys.length) {
            showShipmentStatusChangeConfirmation(selectedRowStatus, paramData)
          } else {
            showShipmentStatusChangeConflictConfirmation(
              selectedRowStatus,
              paramData,
            )
          }
        } else {
          showShipmentStatusChangeConfirmation(selectedRowStatus, paramData)
        }
      }

      setSelectedRowStatus(undefined)
      setSelectedRowKeys([])
    }
  }

  const handleExportShipment = () => {
    const paramData = {
      transactionHeadId: filterTransactionHeadId
        ? Number(filterTransactionHeadId)
        : undefined,
      name: filterName,
      email: filterEmail,
      telephone: filterTelephone,
      address: filterAddress,
      orderDateFrom: filterOrderDateFrom
        ? moment(filterOrderDateFrom).startOf("day").toISOString()
        : undefined,
      orderDateTo: filterOrderDateTo
        ? moment(filterOrderDateTo).endOf("day").toISOString()
        : undefined,
      shipmentStatus: filterShipmentStatus,
    }

    exportShipmentCSVMutation.mutate(paramData)
  }

  const handleImportShipment = () => {
    const paramData = {
      shipments: csvShipments.filter(
        (row) => row?.shipmentId && row?.trackingId,
      ),
    }

    importShipmentsMutation.mutate(paramData)
  }

  const showDetailModal = (shipment) => {
    setCurrentShipment(shipment)
    setIsDetailModalVisible(true)
  }

  const hideDetailModal = () => {
    setCurrentShipment(undefined)
    setIsDetailModalVisible(false)
  }

  const showImportShipmentModal = () => {
    setIsImportShipmentModalVisible(true)
  }

  const hideImportShipmentModal = () => {
    setCsvShipments([])
    setCsvHasError(false)
    setIsImportShipmentModalVisible(false)
  }

  const shipmentColumns = [
    {
      key: "出荷番号",
      title: <p className="whitespace-pre-wrap">{`出荷\n番号`}</p>,
      dataIndex: "shipmentId",
      align: "center",
      width: 50,
    },
    {
      key: "取引番号",
      title: <p className="whitespace-pre-wrap">{`取引\n番号`}</p>,
      dataIndex: "transactionHeadId",
      align: "center",
      width: 50,
    },
    {
      key: "オーダー番号",
      title: <p className="whitespace-pre-wrap">{`注文\n番号`}</p>,
      dataIndex: "terminalTranId",
      align: "center",
      width: 50,
    },
    {
      key: "送り状番号",
      title: <p className="whitespace-pre-wrap">{`送り状\n番号`}</p>,
      dataIndex: "trackingId",
      align: "center",
      width: 60,
    },
    // {
    //   key: "会員コード",
    //   title: <p className="whitespace-pre-wrap">{`会員\nコード`}</p>,
    //   dataIndex: "customerCode",
    //   align: "center",
    //   width: 100,
    // },
    {
      key: "氏名",
      title: "氏名",
      dataIndex: "shipment",
      align: "center",
      width: 120,
      render: (shipment) => (
        <>
          <p>
            {`${shipment?.lastName || "ー"} ${shipment?.firstName || "ー"}`}
          </p>
          <p className="text-xs text-gray-400">
            {`（${shipment?.lastNameKana || "ー"} ${
              shipment?.firstNameKana || "ー"
            }）`}
          </p>
        </>
      ),
    },
    // {
    //   key: "メールアドレス",
    //   title: <p className="whitespace-pre-wrap">{`メール\nアドレス`}</p>,
    //   dataIndex: "shipment",
    //   align: "center",
    //   width: 150,
    //   render: (shipment) => `${shipment?.email || "ー"}`,
    // },
    {
      key: "電話番号",
      title: "電話番号",
      dataIndex: "shipment",
      align: "center",
      width: 110,
      render: (shipment) => `${shipment?.telephone || "ー"}`,
    },
    {
      key: "配達住所",
      title: "配達住所",
      dataIndex: "shipment",
      align: "center",
      width: 150,
      render: (shipment) =>
        `〒${
          shipment?.zipPostal
            ? COMMONS.INSERT_CHARACTER(shipment.zipPostal, 3, "-")
            : ""
        } ${shipment?.address1 || ""}${shipment?.address2 || ""}${
          shipment?.address3 || ""
        }`,
    },
    {
      key: "コメント",
      title: "コメント",
      dataIndex: "comment",
      align: "center",
      width: 100,
    },
    {
      key: "送料",
      title: "送料",
      dataIndex: "shipment",
      align: "center",
      width: 80,
      render: (shipment) =>
        shipment?.shippingFee
          ? `￥${Number(shipment.shippingFee).toLocaleString()}`
          : "ー",
    },
    {
      key: "合計",
      title: "合計",
      dataIndex: "total",
      align: "center",
      width: 80,
      render: (total) => (total ? `￥${Number(total).toLocaleString()}` : "ー"),
    },
    {
      key: "注文日",
      title: "注文日",
      dataIndex: "transactionDateTime",
      align: "center",
      width: 130,
      render: (transactionDateTime) =>
        transactionDateTime
          ? moment(transactionDateTime).format("YYYY年M月D日 HH:mm")
          : "ー",
    },
    {
      key: "配達状態",
      title: "配達状態",
      dataIndex: "shipment",
      align: "center",
      width: 150,
      render: (shipment) =>
        shipment.shipmentStatus ? (
          <Dropdown
            overlay={() => (
              <Menu
                onClick={(e) =>
                  handleStatusChange(
                    shipment.shipmentId,
                    shipment.trackingId,
                    e.key,
                  )
                }
              >
                {COMMONS.SHIPMENT_STATUSES.map((ss) => (
                  <Menu.Item
                    key={ss.value}
                    disabled={!isActionAvailable(shipment.shipmentStatus)}
                    className={
                      isActionAvailable(shipment.shipmentStatus) ? ss.class : ""
                    }
                  >
                    {ss.text}
                  </Menu.Item>
                ))}
              </Menu>
            )}
            trigger={"click"}
          >
            <Button
              className={COMMONS.GET_BUTTON_STYLE_BY_STATUS(
                shipment.shipmentStatus,
              )}
              disabled={!isActionAvailable(shipment.shipmentStatus)}
              loading={updateShipmentMutation.isLoading}
            >
              {COMMONS.SHIPMENT_STATUSES.find(
                (status) => status.value === shipment.shipmentStatus,
              )?.text || "ー"}
              {isActionAvailable(shipment.shipmentStatus) ? (
                <FaChevronDown className="anticon" />
              ) : (
                ""
              )}
            </Button>
          </Dropdown>
        ) : (
          "ー"
        ),
    },
    {
      key: "action",
      title: "",
      dataIndex: "action",
      align: "center",
      width: 50,
      render: (shipment) => (
        <>
          <Tooltip title="詳細" placement="left">
            <Button
              className="m-1"
              icon={<FaEye className="inline-block anticon" />}
              onClick={() => showDetailModal(shipment)}
            />
          </Tooltip>
        </>
      ),
    },
  ]

  const importShipmentColumns = [
    {
      title: "お客様管理番号",
      dataIndex: "shipmentId",
      align: "center",
    },
    {
      title: "お問い合せ送り状No.",
      dataIndex: "trackingId",
      align: "center",
    },
  ]

  const handleTableChange = (paginationData, filters, sorter) => {
    if (paginationData.pageSize !== paginationPerPage) {
      setPaginationPerPage(paginationData.pageSize)
    }

    if (paginationData.current !== paginationPage) {
      setPaginationPage(paginationData.current)
    }

    if (sorter && sorter.order) {
      if (sorter.order === "ascend") {
        if (sorter.order !== paginationSort) {
          setPaginationSort("asc")
        }
      } else if (sorter.order === "descend") {
        if (sorter.order !== paginationSort) {
          setPaginationSort("desc")
        }
      }
    }

    if (sorter && sorter.field) {
      if (sorter.field !== paginationSortKey) {
        setPaginationSortKey(sorter.field)
      }
    }
  }

  const handleFilter = (data) => {
    if (data.filterTransactionHeadId !== filterTransactionHeadId) {
      setFilterTransactionHeadId(data.filterTransactionHeadId)
    }
    if (data.filterName !== filterName) {
      setFilterName(data.filterName)
    }
    if (data.filterEmail !== filterEmail) {
      setFilterEmail(data.filterEmail)
    }
    if (data.filterTelephone !== filterTelephone) {
      setFilterTelephone(data.filterTelephone)
    }
    if (data.filterAddress !== filterAddress) {
      setFilterAddress(data.filterAddress)
    }
    if (data.filterOrderDateFrom !== filterOrderDateFrom) {
      setFilterOrderDateFrom(data.filterOrderDateFrom)
    }
    if (data.filterOrderDateTo !== filterOrderDateTo) {
      setFilterOrderDateTo(data.filterOrderDateTo)
    }
    if (data.filterShipmentStatus !== filterShipmentStatus) {
      setFilterShipmentStatus(data.filterShipmentStatus)
    }
  }

  const onRowSelectChange = (newSelectedRowKeys) => {
    setSelectedRowStatus(undefined)
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const rowSelection = {
    selectedRowKeys,
    columnWidth: 30,
    getCheckboxProps: (record) => ({
      disabled: !isActionAvailable(record?.shipment?.shipmentStatus),
    }),
    onChange: onRowSelectChange,
    selections: COMMONS.SHIPMENT_STATUSES.filter((ss) =>
      isActionAvailable(ss.value),
    ).map((ss) => ({
      key: ss.value,
      text: `${ss.text}を選択`,
      onSelect: (rowKeys) => {
        setSelectedRowKeys(
          shipments
            .filter((s) => rowKeys.includes(s?.Shipment?.shipmentId))
            .filter((s) => s?.Shipment?.shipmentStatus === ss?.value)
            .map((s) => s?.Shipment?.shipmentId),
        )
      },
    })),
  }

  return (
    <>
      <Helmet>
        <title>配達一覧 | {process.env.REACT_APP_SYSTEM_NAME}</title>
      </Helmet>
      <Card
        title="配達一覧"
        className="animate__animated animate__fadeIn"
        bordered={false}
      >
        <Row>
          <Col xs={24}>
            <Form
              form={shipmentsFilterForm}
              onFinish={handleFilter}
              size="large"
              initialValues={{
                filterTransactionHeadId: undefined,
                filterName: undefined,
                filterEmail: undefined,
                filterTelephone: undefined,
                filterAddress: undefined,
                filterOrderDateFrom: undefined,
                filterOrderDateTo: undefined,
                filterShipmentStatus: undefined,
              }}
            >
              <Row gutter={[16, 16]} justify="start">
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="取引番号"
                    name="filterTransactionHeadId"
                    className="mb-4"
                  >
                    <COMMONS.NUMERIC_INPUT
                      placeholder="例：123"
                      allowClear
                      pattern="[0-9]*"
                      inputMode="numeric"
                    />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="氏名・氏名（フリガナ）"
                    name="filterName"
                    className="mb-4"
                  >
                    <Input placeholder="例：山田" allowClear />
                  </Form.Item>
                </Col>
                {/* <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="メールアドレス"
                    name="filterEmail"
                    className="mb-4"
                  >
                    <Input placeholder="例：yamada@gmail.com" allowClear />
                  </Form.Item>
                </Col> */}
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="電話番号"
                    name="filterTelephone"
                    className="mb-4"
                  >
                    <Input placeholder="例：08036412545" allowClear />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="郵便番号 (ハイフン無し)・都道府県・住所"
                    name="filterAddress"
                    className="mb-4"
                  >
                    <Input placeholder="例：愛知県名古屋○○" allowClear />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="注文日から"
                    name="filterOrderDateFrom"
                    className="mb-4"
                  >
                    <CustomDatePicker className="w-full" inputReadOnly />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="注文日まで"
                    name="filterOrderDateTo"
                    className="mb-4 w-full"
                  >
                    <CustomDatePicker className="w-full" inputReadOnly />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="配達状態"
                    name="filterShipmentStatus"
                    className="mb-4"
                  >
                    <Select placeholder="配達状態を選択してください" allowClear>
                      {COMMONS.SHIPMENT_STATUSES.map((ss) => (
                        <Option
                          key={ss.value}
                          value={ss.value}
                          className={ss.class}
                        >
                          {ss.text}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[8, 8]} justify="center mt-8">
                <Col>
                  <Button type="primary" htmlType="submit" className="px-12">
                    検索
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Divider />
        <div className="mb-4">
          {selectedRowKeys.length > 0 ? (
            <div className="flex justify-start">
              <Select
                allowClear
                className="w-50"
                value={selectedRowStatus}
                onChange={setSelectedRowStatus}
                placeholder="配達状態を選択してください"
              >
                {COMMONS.SHIPMENT_STATUSES.map((ss) => (
                  <Option key={ss.value} value={ss.value} className={ss.class}>
                    {ss.text}
                  </Option>
                ))}
              </Select>
              <Button
                type="primary"
                className="ml-1"
                onClick={handleBatchStatusChange}
              >
                保存
              </Button>
            </div>
          ) : (
            <div className="flex justify-start">
              <Button className="mr-1" onClick={showImportShipmentModal}>
                送り状番号CSV
              </Button>
              <Button
                onClick={handleExportShipment}
                loading={exportShipmentCSVMutation.isLoading}
              >
                配達リストCSV
              </Button>
            </div>
          )}
        </div>
        <Tooltip
          visible={isScrollableHintVisible}
          placement="topRight"
          title="⇦スクロールできます⇨"
          overlayStyle={{
            zIndex: 999,
          }}
        >
          <CustomTable
            ref={tableRef}
            bordered
            size="small"
            columns={shipmentColumns}
            locale={{ emptyText: "配達がありません" }}
            onChange={handleTableChange}
            rowSelection={rowSelection}
            scroll={{
              x: 640,
              y: 720,
            }}
            pagination={{
              responsive: true,
              showSizeChanger: true,
              onShowSizeChange: (current, pageSize) =>
                setPaginationPerPage(pageSize),
              current: paginationPage,
              pageSize: paginationPerPage,
              total: paginationTotal,
              showTotal: (total, range) =>
                `全${total}件中${range[0]}～${range[1]}件目`,
              defaultCurrent: 1,
              defaultPageSize: 20,
              position: ["bottomRight"],
            }}
            dataSource={
              shipments
                ? shipments.map((shipment) => {
                    return {
                      key: shipment?.Shipment?.shipmentId,
                      shipmentId: shipment?.Shipment?.shipmentId,
                      transactionHeadId: shipment?.transactionHeadId,
                      terminalTranId: shipment?.terminalTranId,
                      trackingId: shipment?.Shipment?.trackingId || "ー",
                      shipment: shipment?.Shipment,
                      customerCode: shipment?.customerCode,
                      comment: shipment?.comment || "ー",
                      total: shipment?.total,
                      transactionDateTime: shipment?.transactionDateTime,
                      action: shipment,
                    }
                  })
                : []
            }
          />
        </Tooltip>
      </Card>
      <Modal
        title="配達詳細"
        visible={isDetailModalVisible}
        onCancel={hideDetailModal}
        footer={null}
        destroyOnClose
        centered
        width={720}
        bodyStyle={{ maxHeight: "90vh", overflow: "auto" }}
      >
        <Descriptions
          bordered
          layout="vertical"
          title="注文情報"
          column={4}
          className="mb-4"
        >
          <Descriptions.Item label="出荷番号">
            ＃{currentShipment?.Shipment?.shipmentId || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="取引番号">
            ＃{currentShipment?.transactionHeadId || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="注文番号">
            ＃{currentShipment?.terminalTranId || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="送り状番号">
            ＃{currentShipment?.Shipment?.trackingId || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="小計">
            {`￥${
              currentShipment?.subTotal
                ? Number(
                    COMMONS.getHistoryOrderSubtotal({
                      subTotal: currentShipment.subTotal,
                      totalTax: currentShipment?.totalTax,
                      sellDivision: currentShipment?.sellDivision,
                    }),
                  ).toLocaleString()
                : 0
            }`}
          </Descriptions.Item>
          <Descriptions.Item label="送料">
            {`￥${
              currentShipment?.Shipment?.shippingFee
                ? Number(currentShipment.Shipment.shippingFee).toLocaleString()
                : 0
            }`}
          </Descriptions.Item>
          <Descriptions.Item label="利用ポイント">
            {currentShipment?.pointsSpent
              ? `-￥${COMMONS.CONVERT_POINT_TO_CURRENCY({
                  pointSettings,
                  points: currentShipment.pointsSpent,
                }).toLocaleString()}（${Number(
                  currentShipment.pointsSpent,
                ).toLocaleString()}P）`
              : `￥${0}（${0}P）`}
          </Descriptions.Item>
          <Descriptions.Item label="合計" className="font-bold">
            {`￥${
              currentShipment?.total
                ? Number(currentShipment.total).toLocaleString()
                : 0
            }`}
          </Descriptions.Item>
          <Descriptions.Item label="注文日" span={2}>
            {currentShipment?.transactionDateTime
              ? moment(currentShipment.transactionDateTime).format(
                  "YYYY年M月D日 HH:mm",
                )
              : "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="キャンセル日" span={2}>
            {currentShipment?.cancelDateTime
              ? moment(currentShipment.cancelDateTime).format(
                  "YYYY年M月D日 HH:mm",
                )
              : "ー"}
          </Descriptions.Item>
          {/* <Descriptions.Item label="配達時間" span={1}>
            {COMMONS.DELIVERY_TIMES.find(
              (time) => time.id === currentShipment?.Shipment?.deliveryTime,
            )?.start || "ー"}
            {COMMONS.DELIVERY_TIMES.find(
              (time) => time.id === currentShipment?.Shipment?.deliveryTime,
            )?.end
              ? "～" +
                COMMONS.DELIVERY_TIMES.find(
                  (time) => time.id === currentShipment?.Shipment?.deliveryTime,
                )?.end
              : ""}
          </Descriptions.Item> */}
          <Descriptions.Item label="配達状態" span={2}>
            {currentShipment?.Shipment?.shipmentStatus ? (
              <Tag
                color={
                  COMMONS.SHIPMENT_STATUSES.find(
                    (status) =>
                      status.value === currentShipment.Shipment.shipmentStatus,
                  )?.color || ""
                }
              >
                {COMMONS.SHIPMENT_STATUSES.find(
                  (status) =>
                    status.value === currentShipment.Shipment.shipmentStatus,
                )?.text || "ー"}
              </Tag>
            ) : (
              "ー"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="追跡URL" span={2}>
            {currentShipment?.Shipment?.trackingId &&
            systemSettings?.shipmentTrackingUrl?.valueString ? (
              <a
                rel="noopener noreferrer"
                target="_blank"
                className="underline"
                href={systemSettings.shipmentTrackingUrl.valueString.replace(
                  "送り状番号",
                  currentShipment.Shipment.trackingId || "",
                )}
              >
                外部URL
              </a>
            ) : (
              "ー"
            )}
          </Descriptions.Item>
        </Descriptions>
        <Divider />
        <Descriptions title="お届け先情報" bordered column={1} className="mb-4">
          <Descriptions.Item label="氏名">
            {`${currentShipment?.Shipment?.lastName || "ー"} ${
              currentShipment?.Shipment?.firstName || "ー"
            }`}
          </Descriptions.Item>
          <Descriptions.Item label="氏名（カナ）">
            {`${currentShipment?.Shipment?.lastNameKana || "ー"} ${
              currentShipment?.Shipment?.firstNameKana || "ー"
            }`}
          </Descriptions.Item>
          <Descriptions.Item label="電話番号">
            {currentShipment?.Shipment?.telephone || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="配達住所">
            {`〒${
              currentShipment?.Shipment?.zipPostal
                ? COMMONS.INSERT_CHARACTER(
                    currentShipment.Shipment.zipPostal,
                    3,
                    "-",
                  )
                : ""
            } ${currentShipment?.Shipment?.address1 || ""}${
              currentShipment?.Shipment?.address2 || ""
            }${currentShipment?.Shipment?.address3 || ""}`}
          </Descriptions.Item>
          <Descriptions.Item label="コメント">
            <p className="whitespace-pre-wrap">
              {currentShipment?.comment || "ー"}
            </p>
          </Descriptions.Item>
        </Descriptions>
        <Descriptions title="お客様情報" bordered column={1}>
          <Descriptions.Item label="会員コード">
            {currentShipment?.customerCode || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="氏名">
            {`${currentShipment?.Shipper?.lastName || "ー"} ${
              currentShipment?.Shipper?.firstName || "ー"
            }`}
          </Descriptions.Item>
          <Descriptions.Item label="氏名（カナ）">
            {`${currentShipment?.Shipper?.lastNameKana || "ー"} ${
              currentShipment?.Shipper?.firstNameKana || "ー"
            }`}
          </Descriptions.Item>
          <Descriptions.Item label="メールアドレス">
            {currentShipment?.Shipper?.email || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="電話番号">
            {currentShipment?.Shipper?.telephone || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="配達住所">
            {`〒${
              currentShipment?.Shipper?.zipPostal
                ? COMMONS.INSERT_CHARACTER(
                    currentShipment.Shipper.zipPostal,
                    3,
                    "-",
                  )
                : ""
            } ${currentShipment?.Shipper?.address1 || ""}${
              currentShipment?.Shipper?.address2 || ""
            }${currentShipment?.Shipper?.address3 || ""}`}
          </Descriptions.Item>
        </Descriptions>
        <Divider />
        <div className="mb-4">
          <p className="font-bold text-base">商品一覧</p>
        </div>
        <List
          bordered
          dataSource={
            currentShipment?.purchaseDetails &&
            currentShipment?.purchaseDetails.length > 0
              ? currentShipment?.purchaseDetails
              : []
          }
          renderItem={(product) => (
            <List.Item>
              <List.Item.Meta
                avatar={
                  <Image
                    src={
                      products.find(
                        (p) => p?.productCode === product?.productCode,
                      )?.productImages[0]?.picUrl
                        ? `${API.UPLOADS_URL}${
                            products.find(
                              (p) => p?.productCode === product?.productCode,
                            )?.productImages[0]?.picUrl
                          }`
                        : "/default.jpg"
                    }
                    className="rounded object-contain"
                    preview={{
                      mask: <FaEye />,
                      src: products.find(
                        (p) => p?.productCode === product?.productCode,
                      )?.productImages[0]?.picUrl
                        ? `${API.UPLOADS_URL}${
                            products.find(
                              (p) => p?.productCode === product?.productCode,
                            )?.productImages[0]?.picUrl
                          }`
                        : "/default.jpg",
                      maskClassName: "rounded",
                    }}
                    fallback="/default.jpg"
                    width={100}
                    height={50}
                  />
                }
                title={<p>{product?.productCode || ""}</p>}
                description={
                  <>
                    <p className="font-bold">
                      {`${
                        products.find(
                          (p) => p?.productCode === product?.productCode,
                        )?.productName || ""
                      }（${
                        product?.grind === null && product?.roast === null
                          ? `数量：${product?.quantity}`
                          : `${(
                              Number(product?.quantity || 0) * 100
                            ).toLocaleString()}g・${product?.roast || "ー"}・${
                              product?.grind || "ー"
                            }`
                      }）`}
                    </p>
                    <p>
                      {`￥${Number(
                        COMMONS.getHistoryProductTotal({
                          price: product?.price,
                          quantity: 1,
                          taxDivision: product?.taxDivision,
                          sellDivision: currentShipment?.sellDivision,
                          taxRate: currentShipment?.taxRate,
                          taxRounding: currentShipment?.taxRounding,
                        }),
                      ).toLocaleString()} ${
                        product?.grind === null && product?.roast === null
                          ? ""
                          : "(100g)"
                      } × ${product?.quantity || 0}点 ＝ `}
                      <span className="font-bold">{`￥${COMMONS.getHistoryProductTotal(
                        {
                          price: product?.price,
                          quantity: product?.quantity,
                          taxDivision: product?.taxDivision,
                          sellDivision: currentShipment?.sellDivision,
                          taxRate: currentShipment?.taxRate,
                          taxRounding: currentShipment?.taxRounding,
                        },
                      ).toLocaleString()}`}</span>
                    </p>
                  </>
                }
              />
            </List.Item>
          )}
        />
      </Modal>
      <Modal
        title="送り状番号CSVインポート"
        visible={isImportShipmentModalVisible}
        onCancel={hideImportShipmentModal}
        footer={null}
        destroyOnClose
        centered
        width={720}
        bodyStyle={{ maxHeight: "90vh", overflow: "auto" }}
      >
        <CSVReader
          onUploadAccepted={(results) => {
            setCsvHasError(false)

            if (results && results?.data && results?.data?.length > 0) {
              setCsvShipments(
                results.data
                  .filter((row) =>
                    Object.values(row).some((cell) => cell.trim() !== ""),
                  )
                  .map((row) => {
                    if (
                      !row["お問い合せ送り状No."] ||
                      row["お問い合せ送り状No."] === "" ||
                      !row["お客様管理番号"] ||
                      row["お客様管理番号"] === ""
                    ) {
                      setCsvHasError(true)
                    }

                    return {
                      trackingId: row["お問い合せ送り状No."],
                      shipmentId: row["お客様管理番号"],
                    }
                  }),
              )
            } else {
              setCsvShipments([])
            }
          }}
          noDrag
          config={{
            header: true,
          }}
        >
          {({
            getRootProps,
            acceptedFile,
            ProgressBar,
            getRemoveFileProps,
          }) => (
            <div className="flex flex-col">
              <div className="flex justify-center mb-4">
                <div className="bg-yellow-300 rounded p-4">
                  <p>送り状データのCSVをアップロードしてください</p>
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex justify-center">
                  <Button {...getRootProps()}>CSVアップロード</Button>
                </div>
                <div>
                  {acceptedFile ? (
                    <div className="flex flex-col border border-gray-300 rounded p-4 mt-4">
                      <p className="text-base">
                        <FaFileCsv />
                        <span className="ml-2">{acceptedFile.name}</span>
                      </p>
                      <div className="flex justify-center">
                        <Button
                          danger
                          icon={
                            <FaTrashAlt style={{ verticalAlign: "inherit" }} />
                          }
                          {...getRemoveFileProps({
                            onClick: () => {
                              setCsvShipments([])
                              setCsvHasError(false)
                            },
                          })}
                        />
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
                <ProgressBar
                  style={{ backgroundColor: COMMONS.PRIMARY_COLOR }}
                />
              </div>
            </div>
          )}
        </CSVReader>
        <Divider />
        {csvHasError && (
          <div className="flex justify-center mb-4">
            <div className="bg-red-300 rounded p-4">
              <p>
                「お客様管理番号」または「お問い合せ送り状No.」がない行があります。
              </p>
            </div>
          </div>
        )}
        <Table
          bordered
          size="small"
          columns={importShipmentColumns}
          locale={{ emptyText: "データがありません" }}
          scroll={{
            x: 640,
            y: 720,
          }}
          pagination={{
            responsive: true,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `全${total}件中${range[0]}～${range[1]}件目`,
            defaultCurrent: 1,
            defaultPageSize: 10,
            position: ["bottomCenter"],
          }}
          dataSource={
            csvShipments
              ? csvShipments.map((row) => {
                  return {
                    key: row.shipmentId,
                    shipmentId: row.shipmentId || "ー",
                    trackingId: row.trackingId || "ー",
                  }
                })
              : []
          }
        />
        <Divider />
        <div className="flex justify-center">
          <Button
            disabled={csvShipments.length > 0 ? false : true}
            // loading={loadingSendSpin}
            type="primary"
            onClick={handleImportShipment}
          >
            確認する
          </Button>
        </div>
      </Modal>
    </>
  )
}

export default Shipments
